import { useCallback } from 'react';
import DOMPurify from 'dompurify';

/**
 * DOMPurify configuration for plain text sanitization.
 * Strips all HTML tags and attributes to prevent XSS attacks.
 */

const PLAIN_TEXT_CONFIG: DOMPurify.Config = {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
};

/**
 * Custom hook for sanitizing user input in template forms.
 * Prevents XSS attacks by removing all HTML tags and attributes from user input.
 *
 * @returns {Object} Sanitization functions
 * @returns {Function} sanitizePlainText - Sanitizes plain text input by stripping HTML
 *
 * @example
 * const { sanitizePlainText } = useTemplateSanitizers();
 * const clean = sanitizePlainText('<script>alert("xss")</script>Hello');
 * // Returns: 'Hello' (script tag removed)
 */

export function useTemplateSanitizers() {
    /**
     * Sanitizes plain text input by removing all HTML tags and attributes.
     *
     * @param {string} value - The input string to sanitize (default: empty string)
     * @returns {string} Sanitized plain text with all HTML removed
     *
     * @example
     * sanitizePlainText('<b>Bold</b> text') // Returns: 'Bold text'
     * sanitizePlainText('') // Returns: ''
     */
    const sanitizePlainText = useCallback((value: string = '') => {
        try {
            const sanitized = DOMPurify.sanitize(value, PLAIN_TEXT_CONFIG);
            return sanitized;
        } catch {
            return '';
        }
    }, []);

    return { sanitizePlainText };
}
